<script>
// utils and composables
import { login } from '../../api/auth';
import { mapGetters } from 'vuex';
import { parseBoolean } from '@chatwoot/utils';
import { useAlert } from 'dashboard/composables';
import { required, email } from '@vuelidate/validators';
import { useVuelidate } from '@vuelidate/core';
import { SESSION_STORAGE_KEYS } from 'dashboard/constants/sessionStorage';
import SessionStorage from 'shared/helpers/sessionStorage';
import { useBranding } from 'shared/composables/useBranding';

// components
import SimpleDivider from '../../components/Divider/SimpleDivider.vue';
import GoogleOAuthButton from '../../components/GoogleOauth/Button.vue';
import Spinner from 'shared/components/Spinner.vue';
import Icon from 'dashboard/components-next/icon/Icon.vue';
import MfaVerification from 'dashboard/components/auth/MfaVerification.vue';

const ERROR_MESSAGES = {
  'no-account-found': 'LOGIN.OAUTH.NO_ACCOUNT_FOUND',
  'business-account-only': 'LOGIN.OAUTH.BUSINESS_ACCOUNTS_ONLY',
  'saml-authentication-failed': 'LOGIN.SAML.API.ERROR_MESSAGE',
  'saml-not-enabled': 'LOGIN.SAML.API.ERROR_MESSAGE',
};

const IMPERSONATION_URL_SEARCH_KEY = 'impersonation';

export default {
  components: {
    GoogleOAuthButton,
    Spinner,
    SimpleDivider,
    MfaVerification,
    Icon,
  },
  props: {
    ssoAuthToken: { type: String, default: '' },
    ssoAccountId: { type: String, default: '' },
    ssoConversationId: { type: String, default: '' },
    email: { type: String, default: '' },
    authError: { type: String, default: '' },
  },
  setup() {
    const { replaceInstallationName } = useBranding();
    return {
      replaceInstallationName,
      v$: useVuelidate(),
    };
  },
  data() {
    return {
      credentials: {
        email: '',
        password: '',
      },
      loginApi: {
        message: '',
        showLoading: false,
        hasErrored: false,
      },
      error: '',
      mfaRequired: false,
      mfaToken: null,
      // Feature carousel state
      currentFeatureIndex: 0,
      featureInterval: null,
      isFeatureTransitioning: false,
    };
  },
  validations() {
    return {
      credentials: {
        password: {
          required,
        },
        email: {
          required,
          email,
        },
      },
    };
  },
  computed: {
    ...mapGetters({ globalConfig: 'globalConfig/get' }),
    showGoogleOAuth() {
      return Boolean(window.chatwootConfig.googleOAuthClientId);
    },
    showSignupLink() {
      return parseBoolean(window.chatwootConfig.signupEnabled);
    },
    showSamlLogin() {
      return this.globalConfig.isEnterprise && this.globalConfig.isSsoEnabled;
    },
    // Login page customization from globalConfig
    loginBackgroundImage() {
      return this.globalConfig.loginBackgroundImage || null;
    },
    loginTitle() {
      return this.globalConfig.loginPageTitle || 'Bem-vindo de volta';
    },
    loginDescription() {
      return (
        this.globalConfig.loginPageDescription ||
        'Acesse sua plataforma de atendimento ao cliente e continue oferecendo experiencias incriveis.'
      );
    },
    loginOverlayColor() {
      return this.globalConfig.loginOverlayColor || '#1f93ff';
    },
    loginOverlayOpacity() {
      const opacity = this.globalConfig.loginOverlayOpacity;
      return typeof opacity === 'number' ? opacity : 80;
    },
    loginTitleColor() {
      return this.globalConfig.loginTitleColor || '';
    },
    loginDescriptionColor() {
      return this.globalConfig.loginDescriptionColor || '';
    },
    loginFeatures() {
      const features = this.globalConfig.loginPageFeatures;
      if (features && Array.isArray(features) && features.length > 0) {
        return features;
      }
      // Default features
      return [
        {
          icon: 'message-circle',
          title: 'Conversas em tempo real',
          description: 'Responda seus clientes instantaneamente',
        },
      ];
    },
    currentFeature() {
      return (
        this.loginFeatures[this.currentFeatureIndex] || this.loginFeatures[0]
      );
    },
    loginFormPosition() {
      return this.globalConfig.loginFormPosition || 'center';
    },
    formPositionClass() {
      const position = this.loginFormPosition;
      if (position === 'left') {
        return 'justify-start pl-8 lg:pl-16 xl:pl-24';
      }
      if (position === 'right') {
        return 'justify-end pr-8 lg:pr-16 xl:pr-24';
      }
      return 'justify-center';
    },
    backgroundStyle() {
      const defaultImage =
        'https://images.unsplash.com/photo-1557804506-669a67965ba0?q=80&w=2574&auto=format&fit=crop';
      const image = this.loginBackgroundImage || defaultImage;
      return {
        backgroundImage: `url('${image}')`,
        backgroundSize: 'cover',
        backgroundPosition: 'center',
      };
    },
    overlayStyle() {
      // Convert hex to RGB for the gradient
      const hex = this.loginOverlayColor.replace('#', '');
      const r = parseInt(hex.substring(0, 2), 16);
      const g = parseInt(hex.substring(2, 4), 16);
      const b = parseInt(hex.substring(4, 6), 16);
      const opacity = this.loginOverlayOpacity / 100;

      return {
        background: `linear-gradient(to top, rgba(${r}, ${g}, ${b}, ${opacity * 0.9}) 0%, rgba(${r}, ${g}, ${b}, ${opacity * 0.5}) 50%, transparent 100%)`,
      };
    },
    // Brand color for gradient button
    brandColor() {
      return this.globalConfig.brandPrimaryColor || '#1f93ff';
    },
    buttonGradientStyle() {
      const hex = this.brandColor.replace('#', '');
      const r = parseInt(hex.substring(0, 2), 16);
      const g = parseInt(hex.substring(2, 4), 16);
      const b = parseInt(hex.substring(4, 6), 16);
      // Create lighter and darker shades
      const lighterR = Math.min(255, r + 40);
      const lighterG = Math.min(255, g + 40);
      const lighterB = Math.min(255, b + 40);
      const darkerR = Math.max(0, r - 30);
      const darkerG = Math.max(0, g - 30);
      const darkerB = Math.max(0, b - 30);

      return {
        background: `linear-gradient(135deg, rgb(${lighterR}, ${lighterG}, ${lighterB}) 0%, rgb(${r}, ${g}, ${b}) 50%, rgb(${darkerR}, ${darkerG}, ${darkerB}) 100%)`,
      };
    },
  },
  created() {
    if (this.ssoAuthToken) {
      this.submitLogin();
    }
    if (this.authError) {
      const messageKey = ERROR_MESSAGES[this.authError] ?? 'LOGIN.API.UNAUTH';
      const translatedMessage = this.getTranslatedMessage(messageKey);
      this.error = translatedMessage;
      useAlert(translatedMessage);
      this.requestIdleCallbackPolyfill(() => {
        const { query } = this.$route;
        this.$router.replace({ query: { ...query, error: undefined } });
      });
    }
  },
  mounted() {
    // Start feature carousel if more than one feature
    if (this.loginFeatures.length > 1) {
      this.startFeatureCarousel();
    }
  },
  beforeUnmount() {
    // Clean up interval
    this.stopFeatureCarousel();
  },
  methods: {
    startFeatureCarousel() {
      this.featureInterval = setInterval(() => {
        this.nextFeature();
      }, 4000); // 4 seconds
    },
    stopFeatureCarousel() {
      if (this.featureInterval) {
        clearInterval(this.featureInterval);
        this.featureInterval = null;
      }
    },
    nextFeature() {
      this.isFeatureTransitioning = true;
      setTimeout(() => {
        this.currentFeatureIndex =
          (this.currentFeatureIndex + 1) % this.loginFeatures.length;
        this.isFeatureTransitioning = false;
      }, 300); // Match CSS transition duration
    },
    getTranslatedMessage(key) {
      switch (key) {
        case 'LOGIN.OAUTH.NO_ACCOUNT_FOUND':
          return this.$t('LOGIN.OAUTH.NO_ACCOUNT_FOUND');
        case 'LOGIN.OAUTH.BUSINESS_ACCOUNTS_ONLY':
          return this.$t('LOGIN.OAUTH.BUSINESS_ACCOUNTS_ONLY');
        case 'LOGIN.API.UNAUTH':
        default:
          return this.$t('LOGIN.API.UNAUTH');
      }
    },
    requestIdleCallbackPolyfill(callback) {
      if (window.requestIdleCallback) {
        window.requestIdleCallback(callback);
      } else {
        setTimeout(callback, 0);
      }
    },
    showAlertMessage(message) {
      this.loginApi.showLoading = false;
      this.loginApi.message = message;
      useAlert(this.loginApi.message);
    },
    handleImpersonation() {
      const urlParams = new URLSearchParams(window.location.search);
      const impersonation = urlParams.get(IMPERSONATION_URL_SEARCH_KEY);
      if (impersonation) {
        SessionStorage.set(SESSION_STORAGE_KEYS.IMPERSONATION_USER, true);
      }
    },
    submitLogin() {
      this.loginApi.hasErrored = false;
      this.loginApi.showLoading = true;

      const credentials = {
        email: this.email
          ? decodeURIComponent(this.email)
          : this.credentials.email,
        password: this.credentials.password,
        sso_auth_token: this.ssoAuthToken,
        ssoAccountId: this.ssoAccountId,
        ssoConversationId: this.ssoConversationId,
      };

      login(credentials)
        .then(result => {
          if (result?.mfaRequired) {
            this.loginApi.showLoading = false;
            this.mfaRequired = true;
            this.mfaToken = result.mfaToken;
            return;
          }

          this.handleImpersonation();
          this.showAlertMessage(this.$t('LOGIN.API.SUCCESS_MESSAGE'));
        })
        .catch(response => {
          if (this.email) {
            window.location = '/app/login';
          }
          this.loginApi.hasErrored = true;
          this.showAlertMessage(
            response?.message || this.$t('LOGIN.API.UNAUTH')
          );
        });
    },
    submitFormLogin() {
      if (this.v$.credentials.email.$invalid && !this.email) {
        this.showAlertMessage(this.$t('LOGIN.EMAIL.ERROR'));
        return;
      }

      this.submitLogin();
    },
    handleMfaVerified() {
      this.handleImpersonation();
      window.location = '/app';
    },
    handleMfaCancel() {
      this.mfaRequired = false;
      this.mfaToken = null;
      this.credentials.password = '';
    },
    getFeatureIcon(iconName) {
      const iconMap = {
        'message-circle': 'i-lucide-message-circle',
        users: 'i-lucide-users',
        zap: 'i-lucide-zap',
        shield: 'i-lucide-shield',
        globe: 'i-lucide-globe',
        headphones: 'i-lucide-headphones',
        'bar-chart': 'i-lucide-bar-chart',
        clock: 'i-lucide-clock',
        heart: 'i-lucide-heart',
        star: 'i-lucide-star',
        'check-circle': 'i-lucide-check-circle',
        send: 'i-lucide-send',
        inbox: 'i-lucide-inbox',
        smartphone: 'i-lucide-smartphone',
        mail: 'i-lucide-mail',
        bell: 'i-lucide-bell',
        settings: 'i-lucide-settings',
        layers: 'i-lucide-layers',
        award: 'i-lucide-award',
        'trending-up': 'i-lucide-trending-up',
      };
      return iconMap[iconName] || 'i-lucide-message-circle';
    },
  },
};
</script>

<template>
  <main class="relative min-h-screen w-full overflow-hidden">
    <!-- Fullscreen Background Image -->
    <div class="absolute inset-0" :style="backgroundStyle" />

    <!-- Configurable Overlay -->
    <div class="absolute inset-0" :style="overlayStyle" />

    <!-- Floating geometric shapes -->
    <div
      class="absolute top-20 left-20 w-72 h-72 bg-white/10 rounded-full blur-3xl animate-pulse hidden lg:block"
    />
    <div
      class="absolute bottom-20 right-20 w-96 h-96 bg-white/10 rounded-full blur-3xl animate-pulse delay-1000 hidden lg:block"
    />

    <!-- Main Content Container -->
    <div
      class="relative z-10 min-h-screen flex items-center py-8 px-4"
      :class="formPositionClass"
    >
      <!-- Login Card -->
      <div
        class="w-full max-w-md bg-white/40 backdrop-blur-xl rounded-3xl border border-black/10 shadow-2xl p-8"
      >
        <!-- Logo -->
        <div class="mb-6 flex justify-center">
          <img
            :src="globalConfig.logo || '/logo.png'"
            :alt="globalConfig.installationName"
            class="w-auto h-10"
          />
        </div>

        <!-- Title and Description -->
        <div class="mb-6 text-center">
          <h1
            class="text-xl font-bold text-slate-900 mb-1"
            :style="loginTitleColor ? { color: loginTitleColor } : {}"
          >
            {{ loginTitle }}
          </h1>
          <p
            class="text-sm text-slate-600"
            :style="
              loginDescriptionColor ? { color: loginDescriptionColor } : {}
            "
          >
            {{ loginDescription }}
          </p>
        </div>

        <!-- MFA Verification Section -->
        <div v-if="mfaRequired">
          <MfaVerification
            :mfa-token="mfaToken"
            @verified="handleMfaVerified"
            @cancel="handleMfaCancel"
          />
        </div>

        <!-- Regular Login Section -->
        <div
          v-else
          :class="{
            'animate-wiggle': loginApi.hasErrored,
          }"
        >
          <div
            v-if="error"
            class="mb-4 flex items-start gap-2 rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700"
          >
            <Icon icon="i-lucide-circle-alert" class="size-4 mt-0.5 shrink-0" />
            <span>{{ error }}</span>
          </div>
          <div v-if="!email">
            <!-- OAuth and SSO Options -->
            <div class="flex flex-col space-y-3 mb-4">
              <GoogleOAuthButton v-if="showGoogleOAuth" />
              <router-link
                v-if="showSamlLogin"
                to="/app/login/sso"
                class="inline-flex justify-center w-full px-4 py-3 items-center bg-black/5 hover:bg-black/10 rounded-xl border border-black/10 transition-all duration-200"
              >
                <Icon
                  icon="i-lucide-lock-keyhole"
                  class="size-5 text-slate-600"
                />
                <span class="ml-2 text-sm font-medium text-slate-800">
                  {{ $t('LOGIN.SAML.LABEL') }}
                </span>
              </router-link>
            </div>

            <SimpleDivider
              v-if="showGoogleOAuth || showSamlLogin"
              :label="$t('COMMON.OR')"
              class="uppercase mb-4 text-xs text-slate-500"
            />

            <!-- Login Form -->
            <form class="space-y-4" @submit.prevent="submitFormLogin">
              <div class="space-y-1">
                <label class="block text-sm font-medium text-slate-700">
                  {{ $t('LOGIN.EMAIL.LABEL') }}
                </label>
                <div class="relative">
                  <div
                    class="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none"
                  >
                    <Icon
                      icon="i-lucide-at-sign"
                      class="size-4 text-slate-400"
                    />
                  </div>
                  <input
                    v-model="credentials.email"
                    type="email"
                    name="email_address"
                    data-testid="email_input"
                    :tabindex="1"
                    required
                    :placeholder="$t('LOGIN.EMAIL.PLACEHOLDER')"
                    class="w-full pl-10 pr-4 py-3 bg-white/60 border border-black/10 rounded-xl text-slate-900 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-blue-500/30 focus:border-blue-500/50 transition-all"
                    :class="{
                      'ring-2 ring-red-500/50': v$.credentials.email.$error,
                    }"
                    @input="v$.credentials.email.$touch"
                  />
                </div>
              </div>

              <div class="space-y-1">
                <div class="flex justify-between items-center">
                  <label class="block text-sm font-medium text-slate-700">
                    {{ $t('LOGIN.PASSWORD.LABEL') }}
                  </label>
                  <router-link
                    v-if="!globalConfig.disableUserProfileUpdate"
                    to="auth/reset/password"
                    class="text-xs font-medium text-slate-500 hover:text-slate-800 transition-colors"
                    tabindex="4"
                  >
                    {{ $t('LOGIN.FORGOT_PASSWORD') }}
                  </router-link>
                </div>
                <div class="relative">
                  <div
                    class="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none"
                  >
                    <Icon icon="i-lucide-lock" class="size-4 text-slate-400" />
                  </div>
                  <input
                    v-model="credentials.password"
                    type="password"
                    name="password"
                    data-testid="password_input"
                    required
                    :tabindex="2"
                    :placeholder="$t('LOGIN.PASSWORD.PLACEHOLDER')"
                    class="w-full pl-10 pr-4 py-3 bg-white/60 border border-black/10 rounded-xl text-slate-900 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-blue-500/30 focus:border-blue-500/50 transition-all"
                    :class="{
                      'ring-2 ring-red-500/50': v$.credentials.password.$error,
                    }"
                    @input="v$.credentials.password.$touch"
                  />
                </div>
              </div>

              <!-- Gradient Submit Button -->
              <button
                type="submit"
                data-testid="submit_button"
                :tabindex="3"
                :disabled="loginApi.showLoading"
                class="w-full py-3 mt-2 rounded-xl text-white font-semibold shadow-lg hover:shadow-xl transform hover:scale-[1.02] active:scale-[0.98] transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none"
                :style="buttonGradientStyle"
              >
                <span
                  v-if="loginApi.showLoading"
                  class="flex items-center justify-center gap-2"
                >
                  <Spinner size="small" />
                  <span>{{ $t('LOGIN.SUBMIT') }}</span>
                </span>
                <span v-else>{{ $t('LOGIN.SUBMIT') }}</span>
              </button>
            </form>

            <!-- Sign up link -->
            <p
              v-if="showSignupLink"
              class="mt-5 text-center text-xs text-slate-600"
            >
              {{ $t('LOGIN.NO_ACCOUNT') }}
              <router-link
                to="auth/signup"
                class="font-medium text-blue-600 hover:text-blue-800 transition-colors ml-1"
              >
                {{ $t('LOGIN.CREATE_NEW_ACCOUNT') }}
              </router-link>
            </p>
          </div>
          <div v-else class="flex items-center justify-center py-12">
            <Spinner color-scheme="primary" size="" />
          </div>
        </div>

        <!-- Single Feature with Carousel Effect -->
        <div
          v-if="loginFeatures.length > 0 && !email && !mfaRequired"
          class="mt-6 pt-5 border-t border-black/10"
        >
          <div class="relative h-14 overflow-hidden">
            <transition name="slide-up" mode="out-in">
              <div :key="currentFeatureIndex" class="flex items-center gap-3">
                <div
                  class="w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0"
                  :style="{
                    background: `linear-gradient(135deg, ${brandColor}30, ${brandColor}15)`,
                  }"
                >
                  <Icon
                    :icon="getFeatureIcon(currentFeature.icon)"
                    class="size-5 text-slate-700"
                  />
                </div>
                <div class="min-w-0 flex-1">
                  <p class="text-sm font-medium text-slate-800 truncate">
                    {{ currentFeature.title }}
                  </p>
                  <p class="text-xs text-slate-500 truncate">
                    {{ currentFeature.description }}
                  </p>
                </div>
              </div>
            </transition>
          </div>
          <!-- Carousel Indicators -->
          <div
            v-if="loginFeatures.length > 1"
            class="flex justify-center gap-1.5 mt-3"
          >
            <button
              v-for="(_, index) in loginFeatures"
              :key="index"
              type="button"
              class="w-1.5 h-1.5 rounded-full transition-all duration-300"
              :class="
                index === currentFeatureIndex
                  ? 'bg-slate-700 w-4'
                  : 'bg-slate-300 hover:bg-slate-400'
              "
              @click="currentFeatureIndex = index"
            />
          </div>
        </div>
      </div>
    </div>
  </main>
</template>

<style scoped>
/* Slide up transition for feature carousel */
.slide-up-enter-active,
.slide-up-leave-active {
  transition: all 0.3s ease-out;
}

.slide-up-enter-from {
  opacity: 0;
  transform: translateY(20px);
}

.slide-up-leave-to {
  opacity: 0;
  transform: translateY(-20px);
}

/* Custom focus styles for inputs */
input:-webkit-autofill,
input:-webkit-autofill:hover,
input:-webkit-autofill:focus {
  -webkit-text-fill-color: #1e293b;
  -webkit-box-shadow: 0 0 0px 1000px rgba(255, 255, 255, 0.6) inset;
  transition: background-color 5000s ease-in-out 0s;
}
</style>
